import { ReactNode } from 'react';
import { SpecialFunctionString } from '@dreamai/types';

/**
 * Creates a safe empty function to use as a replacement for stringified functions
 * @returns An empty function that doesn't throw errors
 */
export const createSafeFunction = () => () => {};

/**
 * Converts special string identifiers to their corresponding functions
 * @param value - The value to check and potentially convert
 * @returns The original value or a function if the value is a special string
 */
export const convertStringToFunction = (value: any): any => {
  if (typeof value !== 'string') {
    return value;
  }

  switch (value) {
    case SpecialFunctionString.TOOLTIP_PERCENTAGE:
      // Function to display percentage in chart tooltips
      return function (context: any) {
        return context.label + ': ' + context.raw + '%';
      };
    default:
      return value;
  }
};

/**
 * Checks if a value appears to be a stringified function
 * @param value - The value to check
 * @returns True if the value appears to be a stringified function
 */
export const isStringifiedFunction = (value: any): boolean => {
  if (typeof value !== 'string') {
    return false;
  }

  // Common patterns that indicate a stringified function
  const functionPatterns = [
    /^\s*function\s*\(/i, // function()
    /^\s*\(\s*\)\s*=>/i, // () =>
    /^\s*\([^)]*\)\s*=>/i, // (param) =>
    /^\s*[a-zA-Z0-9_$]+\s*=>/i, // param =>
  ];

  return functionPatterns.some((pattern) => pattern.test(value));
};

/**
 * Recursively processes all props to check for stringified functions and convert special strings
 * @param props - The props object to process
 * @returns A new props object with processed values
 */
/**
 * Special case handling for specific props that need to be enforced
 * @param key - The property key
 * @param value - The property value
 * @returns The processed value or undefined if no special handling is needed
 */
export const handleSpecialCaseProps = (
  key: string,
  value: any,
): any | undefined => {
  // Force maintainAspectRatio to always be true in Chart components
  if (key === 'maintainAspectRatio') {
    return true;
  }

  // Handle options.maintainAspectRatio in Chart components
  if (
    key === 'options' &&
    typeof value === 'object' &&
    value !== null &&
    'maintainAspectRatio' in value
  ) {
    return {
      ...value,
      maintainAspectRatio: true,
    };
  }

  return undefined; // No special handling needed
};

export const processProps = (
  props: Record<string, any>,
): Record<string, any> => {
  if (!props || typeof props !== 'object') {
    return props;
  }

  const processedProps: Record<string, any> = {};

  for (const [key, value] of Object.entries(props)) {
    if (isStringifiedFunction(value)) {
      console.warn(`Detected stringified function in prop "${key}":`, value);
      // Replace with a safe empty function to prevent runtime errors
      processedProps[key] = createSafeFunction();
    } else if (
      typeof value === 'object' &&
      value !== null &&
      !Array.isArray(value)
    ) {
      // Recursively process nested objects
      processedProps[key] = processProps(value);
    } else if (Array.isArray(value)) {
      // Process arrays
      processedProps[key] = value.map((item) =>
        typeof item === 'object' && item !== null ? processProps(item) : item,
      );
    } else {
      // Check for special case properties that need enforced values
      const specialCaseValue = handleSpecialCaseProps(key, value);

      if (specialCaseValue !== undefined) {
        processedProps[key] = specialCaseValue;
      } else {
        // Convert special strings to functions
        processedProps[key] = convertStringToFunction(value);
      }
    }
  }

  return processedProps;
};
